import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

interface BrakesSectionProps {
  brakes: {
    frontEffectiveness?: string;
    rearEffectiveness?: string;
  } | null;
  onBrakesChange: (brakes: {
    frontEffectiveness?: string;
    rearEffectiveness?: string;
  }) => void;
}

export const BrakesSection = ({
  brakes,
  onBrakesChange,
}: BrakesSectionProps) => {
  // Check if front and rear are the same (equivalent to "same for all")
  const isSameForAll = Boolean(
    brakes?.frontEffectiveness === brakes?.rearEffectiveness &&
    brakes?.frontEffectiveness &&
    brakes?.rearEffectiveness,
  );

  const [sameForAll, setSameForAll] = useState<boolean>(true);
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const s = ct.pdf?.labels ?? ({} as Record<string, string>);
  const form = ct.form ?? ({} as Record<string, string>);

  const handleEffectivenessChange = (
    effectiveness: string,
    type: "front" | "rear",
  ) => {
    const newData = { ...brakes };

    if (type === "front") {
      newData.frontEffectiveness = effectiveness;
      if (sameForAll) {
        // If "same for all" is checked, update rear to match front
        newData.rearEffectiveness = effectiveness;
      }
    } else if (type === "rear") {
      newData.rearEffectiveness = effectiveness;
      if (sameForAll) {
        // If "same for all" is checked, update front to match rear
        newData.frontEffectiveness = effectiveness;
      }
    }

    onBrakesChange(newData);
  };

  const handleSameForAllChange = (checked: boolean | "indeterminate") => {
    setSameForAll(checked === true);
    const newData = { ...brakes };

    if (checked === true && brakes?.frontEffectiveness) {
      // When checking "same for all", copy front value to rear
      newData.rearEffectiveness = brakes.frontEffectiveness;
    } else if (checked === false) {
      // When unchecking "same for all", clear rear value
      newData.rearEffectiveness = undefined;
    }

    onBrakesChange(newData);
  };

  const effectivenessOptions = ["10-30%", "40-60%", "70-100%"];

  const isComplete = brakes?.frontEffectiveness && brakes?.rearEffectiveness;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-sm mb-3">
          {s.brakeEffectiveness ?? "Bromseffektivitet"}
        </h4>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sameForAllBrakes"
              checked={sameForAll}
              onCheckedChange={handleSameForAllChange}
            />
            <label htmlFor="sameForAllBrakes" className="text-sm">
              {form.sameForAllBrakes ?? "Samma för alla bromsar"}
            </label>
          </div>

          {sameForAll ? (
            <div className="space-y-3">
              <h5 className="font-medium text-sm">
                {s.brakeEffectiveness ?? "Bromseffektivitet"}
              </h5>
              <div className="grid grid-cols-3 gap-1.5">
                {effectivenessOptions.map((effectiveness) => (
                  <Button
                    key={effectiveness}
                    onClick={() =>
                      handleEffectivenessChange(effectiveness, "front")
                    }
                    variant="formOption"
                    className={`${
                      brakes?.frontEffectiveness === effectiveness
                        ? "border-accent border-2"
                        : "border-gray-400"
                    }`}
                  >
                    {effectiveness}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                <h5 className="font-medium text-sm">
                  {form.frontBrakes ?? "Frambromsar"}
                </h5>
                <div className="grid grid-cols-3 gap-1.5">
                  {effectivenessOptions.map((effectiveness) => (
                    <Button
                      key={effectiveness}
                      onClick={() =>
                        handleEffectivenessChange(effectiveness, "front")
                      }
                      variant="formOption"
                      className={`${
                        brakes?.frontEffectiveness === effectiveness
                          ? "border-accent border-2"
                          : "border-gray-400"
                      }`}
                    >
                      {effectiveness}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h5 className="font-medium text-sm">
                  {form.rearBrakes ?? "Bakbromsar"}
                </h5>
                <div className="grid grid-cols-3 gap-1.5">
                  {effectivenessOptions.map((effectiveness) => (
                    <Button
                      key={effectiveness}
                      onClick={() =>
                        handleEffectivenessChange(effectiveness, "rear")
                      }
                      variant="formOption"
                      className={`${
                        brakes?.rearEffectiveness === effectiveness
                          ? "border-accent border-2"
                          : "border-gray-400"
                      }`}
                    >
                      {effectiveness}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
